import { useEffect, useState, useRef, useMemo } from 'react';
import { Car, PersonStanding, Bike, X, Navigation, Clock, Route, ArrowUpDown, Loader2, Search, RefreshCw, Bus, Info, ChevronDown, ChevronUp, Home, Briefcase, Star, Mountain, TrendingUp, TrendingDown, CloudRain } from 'lucide-react';
import { motion, useAnimation, type PanInfo } from 'framer-motion';
import { useDirectionsStore } from '../../stores/directionsStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { preferredModeToTravelMode } from '../../lib/navigationPreferences';
import { formatDistance, formatDuration, type TravelMode } from '../../services/mapbox';
import { ManeuverIcon } from './navigationIcons';
import { getRouteElevation, type RouteElevationStats } from '../../services/navigation';
import { requestCurrentPosition } from '../../utils/geolocationRequest';
import { mapPositionToCoordinates } from '../../utils/geolocation';
import { useMapStore } from '../../stores/mapStore';
import { useAuthStore } from '../../stores/authStore';
import { useVisibleRecentSearches } from '../../hooks/useVisibleRecentSearches';
import { LocationSearchOverlay } from './LocationSearchOverlay';
import { NavigationRegionModal } from './NavigationRegionModal';
import {
  canRequestDirectionsInGhana,
  canStartLiveNavigation,
  getNavigationRegionBlock,
  needsGhanaStartLocationPrompt,
  type NavigationRegionBlock,
} from '../../utils/regionAvailability';
import { isInGhana } from '../../utils/geo';
import { resolveRecentSearchLocation } from '../../utils/recentSearchStorage';
import {
  floodRiskBadgeClass,
  floodRiskLabel,
  FLOOD_EXPOSURE_DISCLAIMER,
  formatFloodExposureSummary,
  type FloodExposureSummary,
} from '../../lib/floodExposure';

interface DirectionsPanelProps {
  originName?: string;
  originCoords?: { lat: number; lng: number };
  destinationName?: string;
  destinationCoords?: { lat: number; lng: number };
  onClose: () => void;
  onStartNavigation: () => void;
}

function stopTypeLabel(type: string): string {
  const map: Record<string, string> = {
    bus_stop: 'Bus Stop',
    bus_station: 'Bus Station',
    trotro_stop: 'Bus Stop',
    taxi_rank: 'Taxi Rank',
    lorry_park: 'Lorry Park',
    rail_station: 'Train Station',
  };
  return map[type] ?? '';
}

export function DirectionsPanel({
  originName = '',
  originCoords,
  destinationName = '',
  destinationCoords,
  onClose,
  onStartNavigation,
}: DirectionsPanelProps) {
  const {
    origin,
    destination,
    routes,
    selectedRouteIndex,
    isLoading,
    error,
    travelMode,
    setOrigin,
    setDestination,
    setTravelMode,
    setSelectedRouteIndex,
    swapLocations,
    clearDirections,
  } = useDirectionsStore();

  const { userLocation, setUserLocation } = useMapStore();
  const recentSearches = useVisibleRecentSearches();
  const { settings } = useSettingsStore();
  const { savedPlaces } = useAuthStore();
  const selectedRoute = routes[selectedRouteIndex];
  const hasAlternateRoutes = routes.length > 1;

  // Phase: 'search' = full page input, 'routes' = bottom sheet with results
  const [phase, setPhase] = useState<'search' | 'routes'>('search');

  // Overlay state
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);
  const [overlayField, setOverlayField] = useState<'origin' | 'destination'>('destination');
  const [stepsExpanded, setStepsExpanded] = useState(false);
  // key: `${routeIdx}-${stepIdx}` → open state for each bus segment's stop sub-list
  const [expandedStepSegments, setExpandedStepSegments] = useState<Record<string, boolean>>({});
  const [routesSheetSnap, setRoutesSheetSnap] = useState<'collapsed' | 'expanded'>('expanded');
  const [routeElevation, setRouteElevation] = useState<RouteElevationStats | null>(null);
  const [elevationLoading, setElevationLoading] = useState(false);
  const elevationAbortRef = useRef<AbortController | null>(null);
  const routesSheetRef = useRef<HTMLDivElement>(null);
  const routesSheetControls = useAnimation();
  const routesSheetDragging = useRef(false);
  const [showGhanaStartModal, setShowGhanaStartModal] = useState(false);
  const [ghanaStartModalVariant, setGhanaStartModalVariant] = useState<
    'start_location' | 'live_navigation'
  >('start_location');
  const [navigationRegionBlock, setNavigationRegionBlock] = useState<NavigationRegionBlock | null>(null);
  const [pendingLocationField, setPendingLocationField] = useState<'origin' | 'destination'>('origin');
  const ghanaStartModalDismissedRef = useRef(false);

  // Refresh GPS when directions opens so Chrome sensor overrides reach origin + prompts.
  useEffect(() => {
    if (!navigator.geolocation) return;
    void requestCurrentPosition()
      .then((pos) => {
        const coords = mapPositionToCoordinates(pos);
        setUserLocation(coords);
        const currentOrigin = useDirectionsStore.getState().origin;
        if (!currentOrigin || currentOrigin.name === 'Your location') {
          useDirectionsStore.getState().setOrigin({
            coordinates: coords,
            name: 'Your location',
          });
        }
      })
      .catch(() => {
        /* permission denied or unavailable — prompt logic handles missing GPS */
      });
  }, [setUserLocation]);

  // Auto-prompt when directions opens with abroad/unavailable start (once per session).
  useEffect(() => {
    if (ghanaStartModalDismissedRef.current) return;
    if (!needsGhanaStartLocationPrompt(userLocation, origin)) return;
    setGhanaStartModalVariant('start_location');
    setPendingLocationField('origin');
    setShowGhanaStartModal(true);
  }, [userLocation, origin]);

  const dismissGhanaStartModal = () => {
    ghanaStartModalDismissedRef.current = true;
    setShowGhanaStartModal(false);
  };

  const isDirectionsRegionBlocked = useMemo(
    () => !canRequestDirectionsInGhana(origin, destination),
    [origin, destination],
  );

  const promptLiveNavigationBlocked = () => {
    ghanaStartModalDismissedRef.current = false;
    setGhanaStartModalVariant('live_navigation');
    setShowGhanaStartModal(true);
  };

  const promptDirectionsRegionBlock = () => {
    const block = getNavigationRegionBlock(
      origin ? { ...origin.coordinates, name: origin.name } : null,
      destination ? { ...destination.coordinates, name: destination.name } : null,
    );
    if (!block) return;
    if (block.reason === 'origin' || block.reason === 'both') {
      ghanaStartModalDismissedRef.current = false;
      setGhanaStartModalVariant('start_location');
      setPendingLocationField('origin');
      setShowGhanaStartModal(true);
      return;
    }
    setNavigationRegionBlock(block);
  };

  const handleGetDirections = () => {
    if (isDirectionsRegionBlocked) {
      promptDirectionsRegionBlock();
      return;
    }
    void useDirectionsStore.getState().fetchDirections({ force: true });
  };

  const homePlace = savedPlaces.find(p => p.label === 'home');
  const workPlace = savedPlaces.find(p => p.label === 'work');
  const otherSaved = savedPlaces.filter(p => p.label === 'saved');

  // Apply preferred mode + endpoints together, then fetch once (avoids stale refetches).
  useEffect(() => {
    const originLocation = originCoords || userLocation;
    const nextOrigin = originLocation
      ? { coordinates: originLocation, name: originName || 'Your location' }
      : null;
    const nextDestination = destinationCoords
      ? { coordinates: destinationCoords, name: destinationName }
      : null;

    if (!nextOrigin && !nextDestination) {
      return;
    }

    const travelMode = preferredModeToTravelMode(settings.preferredMode);

    useDirectionsStore.setState({
      travelMode,
      origin: nextOrigin,
      destination: nextDestination,
      error: null,
    });

    if (nextOrigin && nextDestination) {
      if (canRequestDirectionsInGhana(nextOrigin, nextDestination)) {
        void useDirectionsStore.getState().fetchDirections();
      } else {
        useDirectionsStore.setState({
          routes: [],
          selectedRouteIndex: 0,
          error: null,
          isLoading: false,
        });
      }
    }
  }, [
    settings.preferredMode,
    originCoords,
    destinationCoords,
    userLocation,
    originName,
    destinationName,
  ]);

  // Switch to routes phase when routes arrive
  useEffect(() => {
    if (routes.length > 0 && !isLoading) {
      setPhase('routes');
      setRoutesSheetSnap('expanded');
      setStepsExpanded(routes.length <= 1);
    }
  }, [routes.length, isLoading]);

  const handleRouteCardClick = (index: number) => {
    const route = routes[index];
    if (index === selectedRouteIndex) {
      if (route?.steps.length) {
        setStepsExpanded((prev) => !prev);
      }
      return;
    }
    setSelectedRouteIndex(index);
    if (hasAlternateRoutes) {
      setStepsExpanded(false);
    } else {
      setStepsExpanded(Boolean(route?.steps.length));
    }
  };

  const showElevationStats = travelMode === 'walking' || travelMode === 'cycling';

  useEffect(() => {
    elevationAbortRef.current?.abort();
    setRouteElevation(null);

    if (!selectedRoute || !showElevationStats) {
      setElevationLoading(false);
      return;
    }

    const controller = new AbortController();
    elevationAbortRef.current = controller;
    setElevationLoading(true);

    void getRouteElevation(selectedRoute.geometry, controller.signal)
      .then((stats) => {
        if (!controller.signal.aborted) {
          setRouteElevation(stats);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setRouteElevation(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setElevationLoading(false);
        }
      });

    return () => controller.abort();
  }, [selectedRoute, selectedRouteIndex, showElevationStats, routes]);

  const renderFloodExposureSummary = (summary?: FloodExposureSummary | null) => {
    if (travelMode === 'transit' || !summary?.max_risk_level) return null;

    const headline = formatFloodExposureSummary(summary);
    if (!headline) return null;

    const riskEntries = Object.entries(summary.distance_by_risk ?? {})
      .filter(([, meters]) => (meters ?? 0) > 0)
      .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0));

    return (
      <div className="mt-2 space-y-1.5">
        <div
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium ${floodRiskBadgeClass(summary.max_risk_level)}`}
          title={summary.note ?? FLOOD_EXPOSURE_DISCLAIMER}
        >
          <CloudRain size={14} className="flex-shrink-0" strokeWidth={2.25} />
          <span>{headline}</span>
        </div>
        {riskEntries.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {riskEntries.map(([level, meters]) => (
              <span
                key={level}
                className={`px-2 py-0.5 rounded-md text-[10px] font-medium ${floodRiskBadgeClass(level)}`}
              >
                {floodRiskLabel(level)} · {((meters ?? 0) / 1000).toFixed(1)} km
              </span>
            ))}
          </div>
        )}
        <p className="text-[10px] leading-snug text-gray-400 dark:text-gray-500">
          {summary.note ?? FLOOD_EXPOSURE_DISCLAIMER}
        </p>
      </div>
    );
  };

  const renderElevationSummary = () => {
    if (!showElevationStats) return null;
    if (elevationLoading) {
      return (
        <div className="flex items-center gap-2 mt-2">
          <Loader2 size={14} className="animate-spin text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-400 dark:text-gray-500">Calculating elevation…</span>
        </div>
      );
    }
    if (!routeElevation) return null;

    const ascent = Math.round(routeElevation.totalAscent);
    const descent = Math.round(routeElevation.totalDescent);

    return (
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200/80 dark:border-emerald-700/50"
          title="Total climbing elevation"
        >
          <TrendingUp size={15} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" strokeWidth={2.5} />
          <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 tabular-nums">{ascent} m</span>
          <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">climb</span>
        </div>
        {descent > 0 && (
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-sky-50 dark:bg-sky-900/30 border border-sky-200/80 dark:border-sky-700/50"
            title="Total descending elevation"
          >
            <TrendingDown size={15} className="text-sky-600 dark:text-sky-400 flex-shrink-0" strokeWidth={2.5} />
            <span className="text-sm font-semibold text-sky-800 dark:text-sky-200 tabular-nums">{descent} m</span>
            <span className="text-xs font-medium text-sky-600 dark:text-sky-400">descent</span>
          </div>
        )}
      </div>
    );
  };

  const renderHillAwareBadge = () => {
    if (travelMode !== 'cycling') return null;
    return (
      <div className="px-4 pb-2 flex-shrink-0">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-800/40">
          <Mountain size={12} />
          Hill-aware routing
        </span>
      </div>
    );
  };

  const handleFieldClick = (field: 'origin' | 'destination') => {
    openLocationPicker(field);
  };

  const openLocationPicker = (field: 'origin' | 'destination') => {
    setOverlayField(field);
    setShowLocationOverlay(true);
  };

  const showRegionModalForSelection = (
    field: 'origin' | 'destination',
    location: { coordinates: { lat: number; lng: number }; name: string },
  ) => {
    if (isInGhana(location.coordinates.lng, location.coordinates.lat)) return;
    setPendingLocationField(field);
    ghanaStartModalDismissedRef.current = false;
    if (field === 'destination') {
      setNavigationRegionBlock({ reason: 'destination', destinationName: location.name });
      return;
    }
    setGhanaStartModalVariant('start_location');
    setShowGhanaStartModal(true);
  };

  const handleSelectInGhana = () => {
    setShowGhanaStartModal(false);
    openLocationPicker(pendingLocationField);
  };

  const handleStartNavigationClick = async () => {
    if (isDirectionsRegionBlocked) {
      promptDirectionsRegionBlock();
      return;
    }

    let coords = userLocation;
    if (!coords) {
      try {
        const pos = await requestCurrentPosition();
        coords = mapPositionToCoordinates(pos);
        setUserLocation(coords);
      } catch {
        promptLiveNavigationBlocked();
        return;
      }
    }

    if (!canStartLiveNavigation(coords)) {
      promptLiveNavigationBlocked();
      return;
    }

    const currentOrigin = useDirectionsStore.getState().origin;
    if (!currentOrigin || currentOrigin.name === 'Your location') {
      useDirectionsStore.getState().setOrigin({
        coordinates: coords,
        name: 'Your location',
      });
    }

    onStartNavigation();
  };

  const handleOverlaySelect = (location: { coordinates: { lat: number; lng: number }; name: string }) => {
    const loc = { coordinates: location.coordinates, name: location.name };
    if (overlayField === 'origin') {
      setOrigin(loc);
    } else {
      setDestination(loc);
    }
    setShowLocationOverlay(false);
    showRegionModalForSelection(overlayField, loc);
  };

  const handleOverlayUseCurrentLocation = async () => {
    let coords = userLocation;
    if (!coords) {
      try {
        const pos = await requestCurrentPosition();
        if (pos) {
          coords = mapPositionToCoordinates(pos);
          setUserLocation(coords);
        }
      } catch {
        /* permission denied or unavailable */
      }
    }
    if (coords && isInGhana(coords.lng, coords.lat)) {
      const loc = { coordinates: coords, name: 'Your location' };
      if (overlayField === 'origin') setOrigin(loc);
      else setDestination(loc);
      setShowLocationOverlay(false);
      return;
    }
    setShowLocationOverlay(false);
    setPendingLocationField(overlayField);
    setGhanaStartModalVariant('start_location');
    setShowGhanaStartModal(true);
  };

  const handleSelectSavedPlace = (place: { name: string; coordinates: { lat: number; lng: number } }) => {
    const loc = { coordinates: place.coordinates, name: place.name };
    const field: 'origin' | 'destination' = !origin ? 'origin' : 'destination';
    if (!origin) setOrigin(loc);
    else setDestination(loc);
    showRegionModalForSelection(field, loc);
  };

  const modes: { id: TravelMode; icon: React.ReactNode; label: string; subtitle?: string }[] = [
    { id: 'driving', icon: <Car size={20} />, label: 'Drive' },
    { id: 'walking', icon: <PersonStanding size={20} />, label: 'Walk' },
    { id: 'cycling', icon: <Bike size={20} />, label: 'Cycle' },
    { id: 'transit', icon: <Bus size={20} />, label: 'Transit', subtitle: 'Trotro · Bus' },
  ];

  const ROUTES_SHEET_HEIGHTS = {
    collapsed: 'calc(11.25rem + env(safe-area-inset-bottom, 0px))',
    expanded: '58vh',
  } as const;

  const ROUTES_SHEET_SNAP = {
    collapsed: 0.18,
    expanded: 0.58,
  } as const;

  useEffect(() => {
    if (phase !== 'routes') return;
    routesSheetControls.start({
      y: 0,
      height: ROUTES_SHEET_HEIGHTS[routesSheetSnap],
      transition: { type: 'spring', damping: 30, stiffness: 300 },
    });
  }, [phase, routesSheetSnap, routesSheetControls]);

  const handleRoutesSheetDragStart = () => {
    routesSheetDragging.current = true;
  };

  const handleRoutesSheetDragEnd = (_: unknown, info: PanInfo) => {
    routesSheetDragging.current = false;
    const velocity = info.velocity.y;
    const offset = info.offset.y;
    const currentHeight = routesSheetRef.current?.offsetHeight || 0;
    const viewportHeight = window.innerHeight;
    const draggedRatio = (currentHeight - offset) / viewportHeight;

    if (velocity < -400) {
      setRoutesSheetSnap('expanded');
      return;
    }
    if (velocity > 400) {
      setRoutesSheetSnap('collapsed');
      return;
    }

    const collapsedDist = Math.abs(draggedRatio - ROUTES_SHEET_SNAP.collapsed);
    const expandedDist = Math.abs(draggedRatio - ROUTES_SHEET_SNAP.expanded);
    setRoutesSheetSnap(collapsedDist < expandedDist ? 'collapsed' : 'expanded');
  };

  const handleRoutesSheetHandleClick = () => {
    if (routesSheetDragging.current) return;
    setRoutesSheetSnap((prev) => (prev === 'collapsed' ? 'expanded' : 'collapsed'));
  };

  const handleClose = () => {
    clearDirections();
    onClose();
  };

  const handleBackToEdit = () => {
    setPhase('search');
  };


  // ─── ROUTES PHASE: Bottom sheet with route results ───
  if (phase === 'routes') {
    return (
      <>
        {/* Top bar - compact origin/destination */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-0 left-0 right-0 z-50 safe-area-top"
        >
          <div className="mx-3 mt-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-lg border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
            <div
              className="px-4 py-3.5 cursor-pointer"
              onClick={handleBackToEdit}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                  <div className="w-0.5 h-5 bg-gray-300 dark:bg-gray-600" />
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                </div>
                <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 w-8">From</span>
                    <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{origin?.name || 'Your location'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase font-semibold text-gray-400 dark:text-gray-500 w-8">To</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{destination?.name || 'Destination'}</span>
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); handleBackToEdit(); }} className="p-2 -mr-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom route sheet */}
        <motion.div
          ref={routesSheetRef}
          initial={{ y: '100%' }}
          animate={routesSheetControls}
          exit={{ y: '100%' }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={0.15}
          onDragStart={handleRoutesSheetDragStart}
          onDragEnd={handleRoutesSheetDragEnd}
          style={{ touchAction: 'none' }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl dark:shadow-black/50 flex flex-col overflow-hidden safe-area-bottom"
        >
          <div
            className="absolute left-0 right-0 bg-white dark:bg-gray-900 pointer-events-none"
            style={{ top: '100%', height: '300px' }}
          />
          {/* Drag handle */}
          <div
            className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing flex-shrink-0"
            onClick={handleRoutesSheetHandleClick}
          >
            <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
          </div>

          {routesSheetSnap === 'expanded' && (
            <>
              {/* Travel mode tabs */}
              <div className="flex gap-1 px-4 pb-3 flex-shrink-0">
                {modes.map((m) => (
                  <button
                    key={m.id}
                    data-testid={m.id === 'transit' ? 'travel-mode-transit' : undefined}
                    onClick={() => setTravelMode(m.id)}
                    className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl text-sm font-medium transition-colors ${travelMode === m.id
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                  >
                    {m.icon}
                    <span className="text-[11px] font-medium leading-tight">{m.label}</span>
                    {m.subtitle && (
                      <span className="text-[8px] leading-tight opacity-75 font-normal">{m.subtitle}</span>
                    )}
                  </button>
                ))}
              </div>

              {renderHillAwareBadge()}

              {/* Route content */}
              <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-2 pt-1">
                {isLoading && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 flex flex-col items-center justify-center">
                    <Loader2 size={28} className="text-primary-500 animate-spin mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Finding best route...</p>
                  </div>
                )}

                {error && !isLoading && (
                  <div className="bg-amber-50 dark:bg-amber-900/10 rounded-2xl p-4 flex items-start gap-3 border border-amber-200/60 dark:border-amber-800/30">
                    <Info size={20} className="text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 dark:text-gray-200">Route not available</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        {error.toLowerCase().includes('too long') || error.toLowerCase().includes('too far') || error.toLowerCase().includes('max distance limit')
                          ? 'This distance is too far for the selected travel mode. Try driving instead, or check that both locations are reachable by road.'
                          : error}
                      </p>
                      <button
                        onClick={handleGetDirections}
                        className="mt-2.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:text-amber-800 flex items-center gap-1"
                      >
                        <RefreshCw size={14} /> Try again
                      </button>
                    </div>
                  </div>
                )}

                {/* Route cards */}
                {!isLoading && !error && routes.length > 0 && (
                  <div className="space-y-2">
                    {routes.map((route, index) => (
                      <div
                        key={index}
                        className={`rounded-2xl transition-all overflow-hidden ${index === selectedRouteIndex
                          ? 'bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500'
                          : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                      >
                        {/* Route summary - clickable */}
                        <button
                          type="button"
                          onClick={() => handleRouteCardClick(index)}
                          aria-expanded={index === selectedRouteIndex && route.steps.length > 0 ? stepsExpanded : undefined}
                          className="w-full p-4 text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Clock size={16} className={index === selectedRouteIndex ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'} />
                              <span className={`font-semibold text-lg ${index === selectedRouteIndex ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                                {formatDuration(route.duration)}
                              </span>
                              {route.duration_typical != null && route.duration_typical > route.duration + 30 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  usually {formatDuration(route.duration_typical)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <Route size={14} className="text-gray-400" />
                              <span className="text-sm text-gray-500 dark:text-gray-400">{formatDistance(route.distance, settings.distanceUnit)}</span>
                            </div>
                          </div>
                          {route.summary && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">via {route.summary}</p>
                          )}
                          {index === selectedRouteIndex && renderElevationSummary()}
                          {index === selectedRouteIndex && renderFloodExposureSummary(route.flood_exposure)}
                          <div className="flex items-center justify-between mt-1">
                            {index === 0 && (
                              <p className="text-xs text-primary-600 dark:text-primary-400 font-medium">Fastest route</p>
                            )}
                            {index > 0 && (
                              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Alternative route</p>
                            )}
                            {index === selectedRouteIndex && route.steps.length > 0 && (
                              <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-0.5 ml-auto">
                                {route.steps.length} steps {stepsExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              </span>
                            )}
                          </div>
                        </button>

                        {/* Step-by-step instructions — driving maneuvers + injected stop notifications */}
                        {index === selectedRouteIndex && route.steps.length > 0 && stepsExpanded && (
                          <div className="px-4 pb-4 pt-0">
                            <div className="border-t border-primary-200/50 dark:border-primary-800/30 pt-3">
                              <div className="space-y-0">
                                {route.steps.map((step, stepIdx) => {
                                  // Intermediate transit stops are sub-rows under the bus step preceding them
                                  const isIntermediate = step.mode === 'transit'
                                    && !step.instruction?.startsWith('Board here')
                                    && !step.instruction?.startsWith('Alight here');
                                  if (isIntermediate) return null;

                                  // Collect transit stops immediately following this bus step
                                  const ownedStops: typeof route.steps = [];
                                  if (step.mode === 'bus') {
                                    let j = stepIdx + 1;
                                    while (j < route.steps.length) {
                                      const t = route.steps[j];
                                      if (t.mode === 'transit' && !t.instruction?.startsWith('Board here') && !t.instruction?.startsWith('Alight here')) {
                                        ownedStops.push(t); j++;
                                      } else break;
                                    }
                                  }

                                  const segKey = `${index}-${stepIdx}`;
                                  const segOpen = expandedStepSegments[segKey] ?? false;
                                  const isBoard = step.mode === 'transit' && step.instruction?.startsWith('Board here');
                                  const isAlight = step.mode === 'transit' && step.instruction?.startsWith('Alight here');

                                  return (
                                    <div key={stepIdx}>
                                      {step.mode === 'transit' ? (
                                        <div className={`flex items-start gap-3 py-2 pl-2 border-l-4 rounded-sm ${isBoard ? 'border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10' : isAlight ? 'border-l-orange-500 bg-orange-50/50 dark:bg-orange-900/10' : 'border-l-primary-400 bg-primary-50/40 dark:bg-primary-900/10'}`}>
                                          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isBoard ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : isAlight ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400' : 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'}`}>
                                            <Bus size={14} />
                                          </div>
                                          <div className="flex-1 min-w-0 pt-0.5">
                                            <p className="text-sm text-gray-800 dark:text-gray-200">{step.instruction}</p>
                                            {(isBoard || isAlight) && step.stop_type && (
                                              <p className="text-xs text-primary-500 dark:text-primary-400 mt-0.5">{stopTypeLabel(step.stop_type)}</p>
                                            )}
                                          </div>
                                        </div>
                                      ) : step.mode === 'walking' ? (
                                        <div className="flex items-start gap-3 py-2">
                                          <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                                            <PersonStanding size={14} />
                                          </div>
                                          <div className="flex-1 min-w-0 pt-0.5">
                                            <p className="text-sm text-gray-800 dark:text-gray-200">{step.instruction}</p>
                                            {step.distance > 0 && (
                                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDistance(step.distance, settings.distanceUnit)} · {formatDuration(step.duration)}</p>
                                            )}
                                          </div>
                                        </div>
                                      ) : step.mode === 'driving' ? (
                                        <div className="flex items-start gap-3 py-2">
                                          <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                                            <Car size={14} />
                                          </div>
                                          <div className="flex-1 min-w-0 pt-0.5">
                                            <p className="text-sm text-gray-800 dark:text-gray-200">{step.instruction}</p>
                                            {step.distance > 0 && (
                                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDistance(step.distance, settings.distanceUnit)} · {formatDuration(step.duration)}</p>
                                            )}
                                          </div>
                                        </div>
                                      ) : step.mode === 'bus' ? (
                                        <button
                                          type="button"
                                          onClick={() => setExpandedStepSegments(prev => ({ ...prev, [segKey]: !prev[segKey] }))}
                                          className="w-full flex items-start gap-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 rounded transition-colors text-left"
                                        >
                                          <div className="w-6 h-6 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-500 dark:text-primary-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <Bus size={12} />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-600 dark:text-gray-300">{step.instruction}</p>
                                            {ownedStops.length > 0 && !segOpen && (
                                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                                {ownedStops.length} stop{ownedStops.length !== 1 ? 's' : ''}: {ownedStops.slice(0, 2).map(t => t.name || t.instruction?.replace(/^Next stop:\s*/, '')).filter(Boolean).join(' · ')}{ownedStops.length > 2 ? ` · +${ownedStops.length - 2}` : ''}
                                              </p>
                                            )}
                                          </div>
                                          {ownedStops.length > 0 && (
                                            <span className="mt-0.5 flex-shrink-0 text-gray-400">
                                              {segOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                            </span>
                                          )}
                                        </button>
                                      ) : (
                                        <div className="flex items-start gap-2.5 py-1.5 opacity-60">
                                          <div className="w-6 h-6 rounded bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <ManeuverIcon
                                              type={step.maneuver.type}
                                              modifier={step.maneuver.modifier}
                                              size={14}
                                              className="text-gray-600 dark:text-gray-300"
                                            />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{step.instruction}</p>
                                            {step.distance > 0 && (
                                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDistance(step.distance, settings.distanceUnit)}</p>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {/* Expanded stop sub-list for bus steps */}
                                      {step.mode === 'bus' && segOpen && ownedStops.length > 0 && (
                                        <div className="pl-8 pb-1">
                                          {ownedStops.map((stop, si) => (
                                            <div key={si} className="flex items-center gap-2 py-1 pl-2 border-l border-primary-200 dark:border-primary-800">
                                              <div className="w-1.5 h-1.5 rounded-full bg-primary-300 dark:bg-primary-600 flex-shrink-0" />
                                              <span className="text-xs text-gray-500 dark:text-gray-400 flex-1 truncate">
                                                {stop.name || stop.instruction?.replace(/^Next stop:\s*/, '')}
                                              </span>
                                              {stop.stop_type && stopTypeLabel(stop.stop_type) && (
                                                <span className="text-xs text-primary-400 dark:text-primary-500 flex-shrink-0">{stopTypeLabel(stop.stop_type)}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {routesSheetSnap === 'collapsed' && (
            <div className="px-4 pb-2 flex-shrink-0 min-w-0">
              {isLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Finding best route...</p>
              )}
              {!isLoading && selectedRoute && (
                <>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Clock size={16} className="text-primary-600 dark:text-primary-400 flex-shrink-0" />
                      <span className="font-semibold text-lg text-primary-700 dark:text-primary-300">
                        {formatDuration(selectedRoute.duration)}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {formatDistance(selectedRoute.distance, settings.distanceUnit)}
                    </span>
                  </div>
                  {selectedRoute.summary && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      via {selectedRoute.summary}
                    </p>
                  )}
                  {renderElevationSummary()}
                  {renderFloodExposureSummary(selectedRoute.flood_exposure)}
                  {selectedRouteIndex === 0 && (
                    <p className="text-xs text-primary-600 dark:text-primary-400 font-medium mt-0.5">
                      Fastest route
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Start Journey — always visible; sole focus when collapsed */}
          <div className="px-4 pb-3 pt-2 border-t border-gray-100 dark:border-gray-800 flex-shrink-0 mt-auto">
            <button
              onClick={handleStartNavigationClick}
              disabled={!selectedRoute || isLoading || isDirectionsRegionBlocked}
              className="w-full flex items-center justify-center gap-2 bg-primary-500 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <Navigation size={20} />
              {isLoading ? 'Finding route...' : selectedRoute ? 'Start Journey' : 'Get Directions'}
            </button>
          </div>
        </motion.div>

        <NavigationRegionModal
          open={showGhanaStartModal}
          variant={ghanaStartModalVariant}
          onClose={dismissGhanaStartModal}
          onSelectInGhana={
            ghanaStartModalVariant === 'start_location' ? handleSelectInGhana : undefined
          }
        />
        <NavigationRegionModal
          open={navigationRegionBlock != null}
          variant="navigation_block"
          block={navigationRegionBlock}
          onClose={() => setNavigationRegionBlock(null)}
        />
      </>
    );
  }

  // ─── SEARCH PHASE: Full-page input ───
  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 300 }}
      className="fixed inset-0 bg-white dark:bg-gray-900 z-[100] flex flex-col"
      data-testid="directions-panel"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-4 flex items-center gap-3 z-10 safe-area-top">
        <h2 className="text-lg font-semibold flex-1 dark:text-white">Directions</h2>
        <button
          onClick={handleClose}
          className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Origin & Destination */}
        <div className="p-4 space-y-2">
          {/* Origin */}
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center pt-4 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <div className="w-0.5 h-full min-h-[2rem] bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="flex-1">
              <button
                onClick={() => handleFieldClick('origin')}
                className="w-full bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-3 text-left hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 font-medium">FROM</p>
                <div className="flex items-center gap-2">
                  <p className={`font-medium text-base truncate flex-1 ${origin?.name ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                    {origin?.name || 'Choose starting point'}
                  </p>
                  <Search size={16} className="text-gray-400 flex-shrink-0" />
                </div>
              </button>
            </div>
          </div>

          {/* Swap button */}
          <div className="flex items-center gap-3 pl-1">
            <button
              onClick={swapLocations}
              disabled={!origin || !destination}
              className="w-8 h-8 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-40"
            >
              <ArrowUpDown size={14} className="text-gray-500 dark:text-gray-400" />
            </button>
            <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
          </div>

          {/* Destination */}
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center pt-4 flex-shrink-0">
              <div className="w-3 h-3 rounded-full bg-red-500" />
            </div>
            <div className="flex-1">
              <button
                onClick={() => handleFieldClick('destination')}
                className="w-full bg-gray-100 dark:bg-gray-800 rounded-xl px-4 py-3 text-left hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 font-medium">TO</p>
                <div className="flex items-center gap-2">
                  <p className={`font-medium text-base truncate flex-1 ${destination?.name ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                    {destination?.name || 'Choose destination'}
                  </p>
                  <Search size={16} className="text-gray-400 flex-shrink-0" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Travel Mode Selector */}
        <div className="flex gap-2 px-4 pb-4">
          {modes.map((m) => (
            <button
              key={m.id}
              data-testid={m.id === 'transit' ? 'travel-mode-transit' : undefined}
              onClick={() => setTravelMode(m.id)}
              className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl transition-colors ${travelMode === m.id
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
            >
              {m.icon}
              <span className="text-xs font-medium leading-tight">{m.label}</span>
              {m.subtitle && (
                <span className="text-[9px] leading-tight opacity-75 font-normal">{m.subtitle}</span>
              )}
            </button>
          ))}
        </div>

        {renderHillAwareBadge()}

        {/* Loading / Error in search phase */}
        {isLoading && (
          <div className="px-4 pb-4">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 flex flex-col items-center">
              <Loader2 size={28} className="text-primary-500 animate-spin mb-2" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Finding best route...</p>
            </div>
          </div>
        )}

        {error && !isLoading && (
          <div className="px-4 pb-4">
            <div className="bg-amber-50 dark:bg-amber-900/10 rounded-2xl p-4 flex items-start gap-3 border border-amber-200/60 dark:border-amber-800/30">
              <Info size={18} className="text-amber-500 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm text-gray-800 dark:text-gray-200">Route not available</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {error.toLowerCase().includes('too long') || error.toLowerCase().includes('too far') || error.toLowerCase().includes('max distance limit')
                    ? 'This distance is too far for the selected travel mode. Try driving instead, or check that both locations are reachable by road.'
                    : error}
                </p>
                <button
                  onClick={handleGetDirections}
                  className="mt-2.5 text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1"
                >
                  <RefreshCw size={12} /> Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Saved Places Shortcuts */}
        <div className="px-4 pb-4">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Quick Access</p>
          <div className="flex gap-2">
            {homePlace && (
              <button
                onClick={() => handleSelectSavedPlace({ name: homePlace.name, coordinates: homePlace.coordinates })}
                className="flex-1 flex flex-col items-center gap-1.5 py-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
              >
                <Home size={18} className="text-blue-600 dark:text-blue-400" />
                <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Home</span>
              </button>
            )}
            {workPlace && (
              <button
                onClick={() => handleSelectSavedPlace({ name: workPlace.name, coordinates: workPlace.coordinates })}
                className="flex-1 flex flex-col items-center gap-1.5 py-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
              >
                <Briefcase size={18} className="text-orange-600 dark:text-orange-400" />
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Work</span>
              </button>
            )}
            {otherSaved.length > 0 && (
              <button
                onClick={() => handleSelectSavedPlace({ name: otherSaved[0].name, coordinates: otherSaved[0].coordinates })}
                className="flex-1 flex flex-col items-center gap-1.5 py-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
              >
                <Star size={18} className="text-purple-600 dark:text-purple-400" />
                <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Saved</span>
              </button>
            )}
          </div>
        </div>

        {/* Recent searches */}
        {recentSearches.length > 0 && (
          <div className="px-4 pb-4">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Recent</p>
            <div className="space-y-1">
              {recentSearches.slice(0, 5).map((recent) => (
                <button
                  key={recent.id}
                  onClick={() => {
                    if (!recent.result?.id) return;
                    void resolveRecentSearchLocation(recent.result.id).then((loc) => {
                      if (loc) handleSelectSavedPlace(loc);
                    });
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <Clock size={16} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate">
                      {recent.query}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Location search overlay */}
      {showLocationOverlay && (
        <LocationSearchOverlay
          field={overlayField}
          initialQuery={(() => {
            const name = overlayField === 'origin' ? origin?.name : destination?.name;
            return (name && name !== 'Your location') ? name : '';
          })()}
          userLocation={userLocation}
          savedPlaces={savedPlaces}
          onSelect={handleOverlaySelect}
          onUseCurrentLocation={handleOverlayUseCurrentLocation}
          onClose={() => setShowLocationOverlay(false)}
        />
      )}

      {/* Bottom action */}
      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-800 safe-area-bottom">
        {!origin && !destination && (
          <p className="text-center text-sm text-gray-400 dark:text-gray-500 mb-3">
            Enter starting point and destination
          </p>
        )}
        <button
          onClick={handleGetDirections}
          disabled={!origin || !destination || isLoading || isDirectionsRegionBlocked}
          className="w-full flex items-center justify-center gap-2 bg-primary-500 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-primary-600 transition-colors disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed"
        >
          <Navigation size={20} />
          {isLoading ? 'Finding route...' : 'Get Directions'}
        </button>
      </div>

      <NavigationRegionModal
        open={showGhanaStartModal}
        variant={ghanaStartModalVariant}
        onClose={dismissGhanaStartModal}
        onSelectInGhana={
          ghanaStartModalVariant === 'start_location' ? handleSelectInGhana : undefined
        }
      />
      <NavigationRegionModal
        open={navigationRegionBlock != null}
        variant="navigation_block"
        block={navigationRegionBlock}
        onClose={() => setNavigationRegionBlock(null)}
      />
    </motion.div>
  );
}
